import type { RideSummary, RideTrackPoint } from '@motogo/shared';

import { API_BASE_URL, motogoFetch } from './api';

// The ride data shapes (RideSummary, RideTrackPoint) and pure helpers (mergeRides) live
// in @motogo/shared; this module keeps only the API-coupled fetch functions.

/**
 * Lists the signed-in user's saved rides, newest first. Returns null on 401 (guest or no
 * session) so callers can fall back to local-only rather than treating it as an error.
 */
export async function fetchRides(skip = 0, take = 20): Promise<RideSummary[] | null> {
  const res = await motogoFetch(`${API_BASE_URL}/me/rides?skip=${skip}&take=${take}`);
  if (res.status === 401) return null;
  if (!res.ok) throw new Error(`GET /me/rides failed: ${res.status}`);
  return (await res.json()) as RideSummary[];
}

/** Fetches one ride's summary. Returns null on 401/404. */
export async function fetchRide(id: string): Promise<RideSummary | null> {
  const res = await motogoFetch(`${API_BASE_URL}/me/rides/${encodeURIComponent(id)}`);
  if (res.status === 401 || res.status === 404) return null;
  if (!res.ok) throw new Error(`GET /me/rides/${id} failed: ${res.status}`);
  return (await res.json()) as RideSummary;
}

/** Fetches one ride's full ordered track. Returns null on 401/404. */
export async function fetchRideTrack(id: string): Promise<RideTrackPoint[] | null> {
  const res = await motogoFetch(`${API_BASE_URL}/me/rides/${encodeURIComponent(id)}/track`);
  if (res.status === 401 || res.status === 404) return null;
  if (!res.ok) throw new Error(`GET /me/rides/${id}/track failed: ${res.status}`);
  return (await res.json()) as RideTrackPoint[];
}

/**
 * Appends a chunk of points to a ride via POST /me/rides/{id}/points — the server
 * creates the ride on first contact and dedupes on (ride_id, timestamp), so retrying an
 * unacknowledged chunk (or re-sending a whole ride) is idempotent. Used both for the
 * 30 s mid-ride incremental uploads and for the final/offline sync (all points).
 * Returns the refreshed summary, or null on 401 (not signed in — retried later).
 */
export async function appendRidePoints(
  id: string,
  name: string | null,
  startedAt: string,
  points: RideTrackPoint[],
): Promise<RideSummary | null> {
  const res = await motogoFetch(`${API_BASE_URL}/me/rides/${encodeURIComponent(id)}/points`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, startedAt, points }),
  });
  if (res.status === 401) return null;
  if (!res.ok) throw new Error(`POST /me/rides/${id}/points failed: ${res.status}`);
  return (await res.json()) as RideSummary;
}

